/**
 * Device + app diagnostics for the Help panel's feedback form.
 *
 * The feedback Google Form has a paragraph question, "Device Technical Details"
 * (`entry.858342456`), whose own help text reads: *"These details are
 * automatically provided by the Math MultiApp to help diagnose technical
 * problems. This information does not include anything that can be used to
 * personally identify you."* This module produces exactly that payload and
 * `buildFeedbackUrl` prefills the field with it.
 *
 * **A file cannot be attached.** Google Forms file-upload questions require the
 * respondent to be signed in and cannot be populated from a link — and this
 * question is a text field in any case. The JSON therefore travels as prefilled
 * text, which is what the form was authored to receive.
 *
 * **Nothing identifying goes in.** No username, user id, email, or IP. Share
 * code and activity id are included so support can load the workspace that
 * broke; everything else is browser, hardware and canvas state.
 *
 * ## Deliberately NOT collected, and why
 *
 * The app's users include children, and these fields are individually harmless
 * but jointly a near-unique device fingerprint — which is what turns diagnostics
 * into a persistent identifier for a minor. Three were therefore cut:
 *
 * - **The unmasked GPU renderer string** (`ANGLE (NVIDIA, GeForce RTX 3070…)`)
 *   — the single highest-entropy value a page can read. Replaced by
 *   `softwareRendered`, one bit that answers the question support actually
 *   asks: "is this machine falling back to software rendering?"
 * - **The high-entropy client hints** (CPU architecture, device model, exact OS
 *   and full browser version). The plain UA string already identifies the
 *   browser and OS family well enough to reproduce a bug.
 * - **The storage estimate** (quota and usage). Quota in particular varies by
 *   device and disk, and is a strong discriminator.
 *
 * The cost is real: without the renderer string a GPU-specific rendering bug is
 * harder to pin to a driver, and without the quota a "cannot save" report loses
 * its most direct explanation. That trade was made deliberately in favour of not
 * fingerprinting students.
 *
 * Because both discarded sources were the only async ones, collection is now
 * fully synchronous — which also removes the Safari trap the earlier design had
 * to prime around (Safari expires the user gesture across an `await` and then
 * blocks `window.open`).
 */
/** Bytes → MB, rounded to 1dp. `null` for a missing measurement, so the form
 *  shows an explicit null rather than a misleading 0. */
var mb = function (bytes) {
    return typeof bytes === 'number' ? Math.round((bytes / 1048576) * 10) / 10 : null;
};
/** Answers a CSS media query, or `null` where matchMedia is unavailable. */
var media = function (query) {
    try {
        return window.matchMedia(query).matches;
    }
    catch (_a) {
        return null;
    }
};
/**
 * Coarse GPU capability. Deliberately NOT the unmasked renderer string — see
 * the file header; that string is the strongest fingerprinting signal a page
 * can read. It is queried, reduced to `softwareRendered`, and discarded without
 * ever leaving this function.
 *
 * Wrapped end to end: creating a WebGL context can throw or return null on
 * locked-down or software-rendering machines — exactly the machines whose users
 * file rendering complaints — and a diagnostics helper must never be the reason
 * the feedback link fails to open.
 */
var readGpu = function () {
    var _a, _b;
    var canvas = null;
    var gl = null;
    try {
        canvas = document.createElement('canvas');
        gl = (canvas.getContext('webgl') ||
            canvas.getContext('experimental-webgl'));
        if (!gl)
            return { supported: false };
        var dbg = gl.getExtension('WEBGL_debug_renderer_info');
        // Read locally, reduced to a boolean, never stored. SwiftShader (Chrome),
        // llvmpipe (Mesa) and "software" all mean the GPU is not being used, which
        // is the usual explanation for "the whiteboard is slow".
        var rendererString = String((_a = (dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER))) !== null && _a !== void 0 ? _a : '');
        return {
            supported: true,
            softwareRendered: /swiftshader|llvmpipe|software|basic render/i.test(rendererString),
            // Capability, not identity: a low ceiling explains texture-heavy
            // rendering failures and is shared by whole classes of device.
            maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
        };
    }
    catch (_c) {
        return { supported: false };
    }
    finally {
        // In `finally`, not on the success path: a throwing `getParameter` would
        // otherwise leave the context alive until GC, and browsers cap how many
        // live WebGL contexts a page may hold.
        try {
            (_b = gl === null || gl === void 0 ? void 0 : gl.getExtension('WEBGL_lose_context')) === null || _b === void 0 ? void 0 : _b.loseContext();
        }
        catch (_d) {
            /* releasing is best-effort */
        }
        canvas = null;
    }
};
/**
 * Build the diagnostics object. Fully synchronous — every remaining source is
 * a synchronous read, so the heap figures describe the exact moment the user
 * asked for help, and the click never has to cross an `await`.
 */
export function collectDeviceInfo(app) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0;
    if (app === void 0) { app = {}; }
    var nav = navigator;
    var memory = performance.memory;
    var nowIso = new Date().toISOString();
    // Page load duration, preferring the Navigation Timing Level 2 entry and
    // falling back to the deprecated `timing` object for older Safari.
    var pageLoadMs = null;
    try {
        var navEntry = performance.getEntriesByType('navigation')[0];
        if (navEntry === null || navEntry === void 0 ? void 0 : navEntry.duration) {
            pageLoadMs = Math.round(navEntry.duration);
        }
        else {
            var t = performance.timing;
            if ((t === null || t === void 0 ? void 0 : t.loadEventEnd) && (t === null || t === void 0 ? void 0 : t.navigationStart))
                pageLoadMs = t.loadEventEnd - t.navigationStart;
        }
    }
    catch (_1) {
        /* timing unavailable */
    }
    return {
        capturedAt: nowIso,
        app: {
            version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null,
            builtAt: typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : null,
            // Path only, never the full href: a share code already travels as its own
            // field below, and the query string can carry other state we have not
            // reviewed for this form's privacy promise.
            path: window.location.pathname,
            shareCode: (_a = app.shareCode) !== null && _a !== void 0 ? _a : null,
            activityId: (_b = app.activityId) !== null && _b !== void 0 ? _b : null,
            // activityTitle is deliberately NOT sent. It is user-authored free text
            // and in practice carries real names ("Ms. Garcia - Period 3",
            // "Jayden's fractions"), which would break this form's stated promise
            // that the payload cannot identify anyone. `activityId` already lets
            // support load the workspace, so the title adds nothing they need.
            contentType: app.isSwp ? 'SWP' : app.isIDM ? 'IDM' : 'web',
            isLoggedIn: (_c = app.isLoggedIn) !== null && _c !== void 0 ? _c : null,
            displayMode: (_d = app.displayMode) !== null && _d !== void 0 ? _d : null,
            zoomLevel: (_e = app.zoomLevel) !== null && _e !== void 0 ? _e : null,
            currentPage: (_f = app.currentPage) !== null && _f !== void 0 ? _f : null,
            totalPages: (_g = app.totalPages) !== null && _g !== void 0 ? _g : null,
            canvas: (_h = app.canvasCounts) !== null && _h !== void 0 ? _h : null,
            hasPdfBackground: (_j = app.hasPdfBackground) !== null && _j !== void 0 ? _j : null,
            sessionUptimeMs: Math.round(performance.now()),
            pageLoadMs: pageLoadMs,
        },
        // Kept deliberately small. The UA string already states the browser, its
        // version, the OS and whether it is mobile, so `vendor`, `languages` and the
        // three `uaData*` mirrors were dropped rather than sending the same fact
        // three ways — every field here has to earn its place in a URL-borne
        // payload. High-entropy client hints are not requested at all; see header.
        browser: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            // Both explain a failure to save or load rather than a rendering fault.
            cookiesEnabled: navigator.cookieEnabled,
            online: navigator.onLine,
        },
        hardware: {
            // `deviceMemory` is the browser's coarse RAM hint in GB, Chromium-only and
            // deliberately capped at 8 — it is NOT the machine's true RAM, and a 64GB
            // workstation also reports 8. Named so nobody reads it as the real figure.
            approxDeviceMemoryGB: (_k = nav.deviceMemory) !== null && _k !== void 0 ? _k : null,
            logicalCores: (_l = navigator.hardwareConcurrency) !== null && _l !== void 0 ? _l : null,
            maxTouchPoints: (_m = navigator.maxTouchPoints) !== null && _m !== void 0 ? _m : null,
            gpu: readGpu(),
        },
        // How much memory THIS app is using. Chromium-only; null elsewhere.
        appMemory: memory
            ? {
                usedHeapMB: mb(memory.usedJSHeapSize),
                totalHeapMB: mb(memory.totalJSHeapSize),
                heapLimitMB: mb(memory.jsHeapSizeLimit),
                heapUsedPercent: memory.jsHeapSizeLimit
                    ? Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100)
                    : null,
            }
            : null,
        // No storage estimate: quota is a strong device discriminator. See header.
        // Screen size, the viewport actually rendered into, and the ratio between
        // them. `availWidth`/`availHeight`, `colorDepth` and the outer window size
        // were dropped: they almost never distinguish two reports, and the
        // viewport is what layout complaints are really about.
        screen: {
            width: (_p = (_o = window.screen) === null || _o === void 0 ? void 0 : _o.width) !== null && _p !== void 0 ? _p : null,
            height: (_r = (_q = window.screen) === null || _q === void 0 ? void 0 : _q.height) !== null && _r !== void 0 ? _r : null,
            pixelRatio: (_s = window.devicePixelRatio) !== null && _s !== void 0 ? _s : null,
            orientation: (_v = (_u = (_t = window.screen) === null || _t === void 0 ? void 0 : _t.orientation) === null || _u === void 0 ? void 0 : _u.type) !== null && _v !== void 0 ? _v : null,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        },
        network: nav.connection
            ? {
                effectiveType: (_w = nav.connection.effectiveType) !== null && _w !== void 0 ? _w : null,
                downlinkMbps: (_x = nav.connection.downlink) !== null && _x !== void 0 ? _x : null,
                rttMs: (_y = nav.connection.rtt) !== null && _y !== void 0 ? _y : null,
                saveData: (_z = nav.connection.saveData) !== null && _z !== void 0 ? _z : null,
            }
            : null,
        environment: {
            // Time zone only — the offset is derivable from it, and `capturedAt` is
            // already an absolute timestamp for correlating against server logs.
            timeZone: (_0 = Intl.DateTimeFormat().resolvedOptions().timeZone) !== null && _0 !== void 0 ? _0 : null,
            // Windows High Contrast Mode. Kept even though NOTHING in this app reads
            // `forced-colors` — which is precisely the point: the browser overrides
            // the page's colours without the app's participation, so a user in that
            // mode sees genuinely broken rendering and nothing else in this payload
            // would explain it.
            forcedColors: media('(forced-colors: active)'),
            // `(pointer: coarse)`, matching IS_TOUCH_DEVICE in HelpTool /
            // ManipulativeTool / ContextMenu — NOT `any-pointer`, which answers a
            // different question. A desktop with a touchscreen attached reports
            // coarse for `any-pointer` while the app still takes the desktop path,
            // so reporting that flavour would describe a branch the user never got.
            // This says which menu/ARIA semantics they actually saw.
            touchPrimary: media('(pointer: coarse)'),
        },
    };
}
/**
 * Cap on the encoded prefill value.
 *
 * The payload rides in the URL, and an over-long URL is rejected outright
 * rather than truncated — losing the whole form, not just the extra detail.
 *
 * 7000 rather than a larger figure: a measured pretty-printed payload encodes
 * to roughly 5700 characters, and while Google itself accepts far more, the
 * classic 8KB request-line limit is still enforced by some school proxies —
 * exactly the networks this app runs on. The cap is set so a payload heavier
 * than typical (many pages, long UA and language lists) degrades to compact
 * JSON *before* it reaches that zone, instead of after.
 */
var MAX_ENCODED_LENGTH = 7000;
/**
 * Build the feedback URL with the device details prefilled.
 *
 * Degrades in steps rather than all at once: pretty-printed JSON (readable to
 * whoever reads the form response), then compact, then a truncated compact
 * payload marked as such. If even the truncated form will not fit, the bare
 * form URL is returned — the user must always be able to send feedback, with or
 * without diagnostics.
 */
export function buildFeedbackUrl(baseUrl, entryId, app) {
    if (app === void 0) { app = {}; }
    try {
        var info = collectDeviceInfo(app);
        var value = JSON.stringify(info, null, 2);
        if (encodeURIComponent(value).length > MAX_ENCODED_LENGTH)
            value = JSON.stringify(info);
        if (encodeURIComponent(value).length > MAX_ENCODED_LENGTH) {
            // Truncate on the decoded string, then re-check: a multi-byte character
            // can expand to nine encoded characters, so slicing to the cap directly
            // would not guarantee a fit.
            value = "".concat(value.slice(0, 2000), "\n\u2026truncated");
            if (encodeURIComponent(value).length > MAX_ENCODED_LENGTH)
                return baseUrl;
        }
        var separator = baseUrl.includes('?') ? '&' : '?';
        // `usp=pp_url` is what tells Forms to treat the entry params as a prefill.
        return "".concat(baseUrl).concat(separator, "usp=pp_url&").concat(encodeURIComponent(entryId), "=").concat(encodeURIComponent(value));
    }
    catch (_a) {
        // Diagnostics must never cost the user their feedback link.
        return baseUrl;
    }
}
